import { useState, useEffect } from 'react';
import api from '../lib/api';
import { DATA_IMPORT_JOB_HISTORY_LIMIT } from '../constants/dataImportLimits';

export interface DataSourceFile {
  id: string;
  name: string;
  key: string;
  size: number;
  uploaded_at: string;
  content_type: string;
  uploaded_by: string;
  last_imported?: string;
}

export interface DataSource {
  id: number;
  name: string;
  description: string;
  type: string;
  status: string;
  import_type?: string;
  schedule?: string;
  last_load_time: string | null;
  record_count?: number;
  file_references?: DataSourceFile[];
  created_at: string;
  updated_at: string;
  // API-related properties
  api_status?: string;
  rate_limit_used?: number;
  rate_limit_max?: number;
  last_api_test?: string;
  config?: any;
}

export interface JobHistory {
  id: number;
  source_id: number;
  source_name: string;
  job_type: string;
  status: string;
  start_time: string;
  end_time: string | null;
  duration_seconds: number | null;
  record_count: number | null;
  error_summary: string | null;
  error_details: string | null;
  /** JSON from API (`jsonb`); shape varies by `job_type`. */
  metadata: unknown;
  created_at: string;
}

export interface DataStats {
  totalSources: number;
  successfulJobs: number;
  failedJobs: number;
  avgDuration: string;
}

export const useDataManagement = () => {
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [jobHistory, setJobHistory] = useState<JobHistory[]>([]);
  const [stats, setStats] = useState<DataStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetchDataSources = async () => {
    try {
      const sourcesResponse = await api.get('/data-management/sources', {
        headers: { 'Cache-Control': 'no-cache' }
      });
      setDataSources(sourcesResponse.data.data.sources);
    } catch (err: any) {
      console.error('Error refetching data sources:', err);
    }
  };

  const refetchJobHistory = async () => {
    try {
      const historyResponse = await api.get('/data-management/jobs', {
        params: { limit: DATA_IMPORT_JOB_HISTORY_LIMIT },
        headers: { 'Cache-Control': 'no-cache' },
      });
      setJobHistory(historyResponse.data.data.jobs);
    } catch (err: any) {
      console.error('Error refetching job history:', err);
    }
  };

  const refetchStats = async () => {
    try {
      const statsResponse = await api.get('/data-management/stats', {
        headers: { 'Cache-Control': 'no-cache' }
      });
      setStats(statsResponse.data.data);
    } catch (err: any) {
      console.error('Error refetching data stats:', err);
    }
  };

  const refetchAll = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await Promise.all([refetchDataSources(), refetchJobHistory(), refetchStats()]);
    } catch (err: any) {
      console.error('Error refetching data management data:', err);
      setError(err.response?.data?.message || 'Failed to reload data');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch all data on component mount
  useEffect(() => {
    refetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create a new data source
  const createDataSource = async (data: Partial<DataSource>) => {
    try {
      const response = await api.post('/data-management/sources', data);
      setDataSources(prev => [...prev, response.data.data]);
      return response.data.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to create data source');
    }
  };

  // Update an existing data source
  const updateDataSource = async (id: number, data: Partial<DataSource>) => {
    try {
      const response = await api.put(`/data-management/sources/${id}`, data);
      setDataSources(prev => prev.map(source => 
        source.id === id ? response.data.data : source
      ));
      return response.data.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to update data source');
    }
  };

  // Delete a data source
  const deleteDataSource = async (id: number) => {
    try {
      await api.delete(`/data-management/sources/${id}`);
      setDataSources(prev => prev.filter(source => source.id !== id));
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to delete data source');
    }
  };

  // Trigger a manual job
  const triggerManualJob = async (sourceId: number, jobType: string) => {
    try {
      const response = await api.post('/data-management/sources/' + sourceId + '/trigger', {
        job_type: jobType
      });
      
      // Refresh job history after triggering
      const historyResponse = await api.get('/data-management/jobs', {
        params: { limit: DATA_IMPORT_JOB_HISTORY_LIMIT },
      });
      setJobHistory(historyResponse.data.data.jobs);
      
      return response.data.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to trigger job');
    }
  };

  // Upload file to data source
  const uploadFileToDataSource = async (sourceId: number, file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/data-management/sources/${sourceId}/upload-file`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Refresh data sources to get updated file list
      const sourcesResponse = await api.get('/data-management/sources');
      setDataSources(sourcesResponse.data.data.sources);

      return response.data.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to upload file');
    }
  };

  // Delete file from data source
  const deleteFileFromDataSource = async (sourceId: number, fileId: string) => {
    try {
      await api.delete(`/data-management/sources/${sourceId}/files/${fileId}`);

      // Refresh data sources to get updated file list
      const sourcesResponse = await api.get('/data-management/sources');
      setDataSources(sourcesResponse.data.data.sources);
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to delete file');
    }
  };

  // Download file
  const downloadFile = async (fileKey: string, fileName: string) => {
    try {
      const response = await api.get(`/data-management/files/${encodeURIComponent(fileKey)}/download`);
      const downloadUrl = response.data.data.download_url;
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to download file');
    }
  };



  // Get bills
  const getBills = async (params?: { limit?: number; offset?: number; search?: string }) => {
    try {
      const response = await api.get('/data-management/bills', { params });
      return response.data.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to fetch bills');
    }
  };

  // Get bill details
  const getBillDetails = async (billId: number) => {
    try {
      const response = await api.get(`/data-management/bills/${billId}`);
      return response.data.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to fetch bill details');
    }
  };

  // Trigger manual job for a data source
  // options: optional parameters for specific data sources (e.g., calendar targetDate, forceRefresh; bills forceReimport; campaign finance forceBackfill)
  const triggerDataSourceJob = async (sourceId: string, options?: { targetDate?: string; forceRefresh?: boolean; forceReimport?: boolean; forceBackfill?: boolean }) => {
    try {
      const response = await api.post(`/data-management/sources/${sourceId}/trigger`, options || {});
      return response.data.data;
    } catch (err: any) {
      // Check if it's a job queue conflict (409 status)
      if (err.response?.status === 409) {
        const errorData = err.response.data;
        throw {
          isQueueConflict: true,
          reason: errorData.reason,
          message: errorData.message || errorData.error,
          runningJob: errorData.runningJob,
          queueStatus: errorData.queueStatus,
          actionRequired: errorData.action_required
        };
      }
      throw new Error(err.response?.data?.error || 'Failed to trigger data source job');
    }
  };

  // Get queue status for a data source
  const getDataSourceQueueStatus = async (sourceId: string) => {
    try {
      const response = await api.get(`/data-management/sources/${sourceId}/queue`);
      return response.data.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to get queue status');
    }
  };

  // Clean up stuck jobs (admin only)
  const cleanupStuckJobs = async (maxAgeHours = 24) => {
    try {
      const response = await api.post('/data-management/cleanup-stuck-jobs', { maxAgeHours });
      return response.data.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to cleanup stuck jobs');
    }
  };

  // Pause/Resume a data source
  const toggleDataSourceStatus = async (sourceId: string, status: 'Active' | 'Paused') => {
    try {
      const response = await api.patch(`/data-management/sources/${sourceId}/status`, { status });
      return response.data.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to update data source status');
    }
  };

  // Get job logs for a specific data source
  const getDataSourceLogs = async (sourceId: string, limit = 10) => {
    try {
      const response = await api.get(`/data-management/sources/${sourceId}/logs`, {
        params: { limit }
      });
      return response.data.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to fetch data source logs');
    }
  };

  // Get logs for a specific job
  const getJobLogs = async (jobId: string) => {
    try {
      const response = await api.get(`/data-management/jobs/${jobId}`);
      return response.data.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to fetch job logs');
    }
  };

  // Get import-ready files for a data source
  const getImportReadyFiles = async (sourceId: string) => {
    try {
      const response = await api.get(`/data-management/sources/${sourceId}/import-ready-files`);
      return response.data.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to get import-ready files');
    }
  };

  return {
    dataSources,
    jobHistory,
    stats,
    isLoading,
    error,
    createDataSource,
    updateDataSource,
    deleteDataSource,
    triggerManualJob,
    uploadFileToDataSource,
    deleteFileFromDataSource,
    downloadFile,
    getImportReadyFiles,
    getBills,
    getBillDetails,
    triggerDataSourceJob,
    getDataSourceQueueStatus,
    cleanupStuckJobs,
    toggleDataSourceStatus,
    getDataSourceLogs,
    getJobLogs,
    refetchDataSources,
    refetchJobHistory,
    refetchStats,
    refetchAll,
  };
}; 